import cx from 'classnames';
import NextLink from 'next/link';
import pluralize from 'pluralize';

import {Icon, NumberTicker, Rings, Text} from '#component/base';
import {KitRegisteredPill} from '#component/partial';
import {sprinkles} from '#style/sprinkles';

import {TallyCardProps} from './MyTallyAgeCard.types';

import * as css from './MyTallyAgeCard.css';
const getRingsVariantForDeltaAge = (deltaAge = 0) => {
  if (deltaAge === 0) {
    return 'double';
  }

  return deltaAge > 0 ? 'grow' : 'shrink';
};

export const MyTallyAgeCard = ({
  labAssessment,
  registeredDate,
  columnSpan = [8, 4],
}: TallyCardProps) => {
  const {formattedBioAge, formattedChronologicalAge, deltaAge = 0} = labAssessment || {};
  const {years: bioAgeYears = 0, months: bioAgeMonths = 0} = formattedBioAge || {};
  const {years: chronoAgeYears = 0} = formattedChronologicalAge || {};
  const isEqual = deltaAge === 0;
  const isYounger = deltaAge < 0;

  return (
    <NextLink href="/mytallyage" passHref>
      <a className={cx(css.tallyCardWrapper, sprinkles({colSpan: columnSpan}))}>
        <div className={sprinkles({flex: 'flex'})}>
          <Text as="h4" color="cotton" variant="heading3" className={css.headingAge}>
            {'My TallyAge™'}
          </Text>
          {registeredDate && labAssessment ? (
            <KitRegisteredPill
              className={sprinkles({display: ['none', 'none', 'flex'], mL: 'auto'})}
              registeredDate={registeredDate}
              variant="primaryDark">
              {' results'}
            </KitRegisteredPill>
          ) : (
            <KitRegisteredPill
              className={sprinkles({display: ['none', 'none', 'flex'], mL: 'auto'})}
              variant="primaryDark">
              {' Awaiting Results'}
            </KitRegisteredPill>
          )}
        </div>
        <div className={sprinkles({items: 'start', pos: 'relative', w: 'maxContent', z: 0})}>
          <Rings className={css.rings} variant={getRingsVariantForDeltaAge(deltaAge)} />
          {labAssessment && (
            <>
              <div className={css.ageContainer}>
                <NumberTicker
                  appear
                  className={css.age}
                  color="white"
                  fontWeight="ultraLight"
                  initialValue={chronoAgeYears}>
                  {bioAgeYears}
                </NumberTicker>
                <div className={css.ageSubtext}>
                  <Icon
                    name={isEqual ? 'ArrowsInSimple' : isYounger ? 'TrendDown' : 'TrendUp'}
                    color="peach"
                  />
                  <Text color="white" variant="bodyLarge">
                    {pluralize('year', bioAgeYears)}
                    {bioAgeMonths > 0 && (
                      <>
                        {' & '}
                        <br />
                        {pluralize('month', bioAgeMonths, true)}
                      </>
                    )}
                  </Text>
                </div>
              </div>
              <div className={sprinkles({dir: 'col', flex: 'flex', gap: 48})}>
                <Text
                  className={sprinkles({flex: 'flex', gap: 16, items: 'center'})}
                  color="cotton"
                  variant="button">
                  Learn More
                  <Icon name="ArrowRight" color="cotton" size="small" />
                </Text>
              </div>
            </>
          )}
        </div>
        {!labAssessment && (
          <div className={sprinkles({dir: 'col', flex: 'flex', gap: 48})}>
            <Text
              style={{fontSize: '24px'}}
              className={sprinkles({flex: 'flex', gap: 16, items: 'center'})}
              color="white"
              variant="heading3">
              We are still processing your TallyAge™ Test. When complete, your results will appear
              here
            </Text>
          </div>
        )}
      </a>
    </NextLink>
  );
};
